<template>
    <div class="pdf-viewer w-full" role="document" :aria-label="`PDF document: ${pdfTitle}`">
        <div class="flex justify-end items-center gap-2 mb-4">
            <button
                type="button"
                class="p-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                aria-haspopup="dialog"
                :disabled="loading"
                @click="openDownloadModal"
            >
                <span>Download</span>
                <font-awesome :icon="['far', 'download']" class="w-5 h-5" aria-hidden="true" />
            </button>
        </div>
        <div class="relative w-full" :style="{ minHeight: iframeHeight }">
            <div v-if="loading" class="absolute inset-0 flex justify-center items-center">
                <div class="flex items-center gap-2" aria-live="polite" aria-busy="true">
                    <span class="sr-only">{{ processingAccessible ? 'Processing accessible PDF...' : 'Loading PDF...' }}</span>
                    <div class="h-3 w-3 rounded-full bg-gray-700 animate-bounce" style="animation-delay: 0ms"></div>
                    <div class="h-3 w-3 rounded-full bg-gray-700/80 animate-bounce" style="animation-delay: 150ms"></div>
                    <div class="h-3 w-3 rounded-full bg-gray-700/60 animate-bounce" style="animation-delay: 300ms"></div>
                </div>
            </div>
            <div v-if="error" class="absolute inset-0 flex justify-center items-center" role="alert">
                <div class="text-red-600">{{ error }}</div>
            </div>
            <AdobePdfViewer
                v-if="!loading && !error && adobeEnabled && !adobeFailed"
                :key="`pdf-adobe-${componentId}-${accessibleEnabled}`"
                :src="rawPdfUrl"
                :pdf-title="pdfTitle"
                container-class="w-full border border-gray-200 rounded-lg shadow-lg"
                :container-style="{ height: iframeHeight }"
                :show-download="false"
                @failed="adobeFailed = true"
            />
            <iframe
                v-else-if="!loading && !error"
                :key="`pdf-iframe-${componentId}-${accessibleEnabled}`"
                :src="currentPdfUrl"
                :title="`PDF document: ${pdfTitle}`"
                class="w-full border border-gray-200 rounded-lg shadow-lg"
                :style="{ height: iframeHeight }"
                @load="handleIframeLoad"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import AdobePdfViewer from '~/components/pdf/AdobePdfViewer.vue';
import DownloadPdfModal from '~/components/modals/DownloadPdfModal.vue';

const props = defineProps({
    src: {
        type: String,
        required: true
    },
    pdfTitle: {
        type: String,
        default: 'Document'
    },
    pdfLang: {
        type: String,
        default: 'en-US'
    },
    height: {
        type: String,
        default: '80vh'
    },
    siteSlug: {
        type: String,
        default: null
    },
    pageSlug: {
        type: String,
        default: null
    },
    pageType: {
        type: String,
        default: null
    }
});

// Accessible PDF preference (shared cookie across subdomains)
const { accessibleEnabled, featureEnabled: accessibleFeatureEnabled } = useAccessiblePdfPreference();

// Generate unique ID for this component instance
const componentId = `pdf-${Math.random().toString(36).substring(2, 9)}`;

const loading = ref(true);
const error = ref('');
const processingAccessible = ref(false);
const currentAccessibleUrl = ref(null);

// Iframe height - use prop value
const iframeHeight = computed(() => props.height);

// Convert full URL to relative path for proxy
const getProxiedUrl = (url) => {
    if (url.includes('://')) {
        const urlObj = new URL(url);
        return urlObj.pathname + urlObj.search + urlObj.hash;
    }
    return url;
};

// Append PDF Open Parameters to hide the native browser PDF toolbar and
// fit the page width to the viewer on load (fixes mobile zoom-in issue).
// Works in Chromium (PDFium) and Firefox (PDF.js); Safari ignores both.
// Safe with signed S3 URLs because fragments are never sent to the server.
const withPdfViewerParams = (url) => {
    if (!url) return url;
    return url + (url.includes('#') ? '&' : '#') + 'toolbar=0&view=FitH';
};

// Raw PDF URL without viewer params — used by AdobePdfViewer (which is
// configured via JS, not URL fragments).
const rawPdfUrl = computed(() => {
    if (accessibleEnabled.value && currentAccessibleUrl.value) {
        return currentAccessibleUrl.value;
    }
    return getProxiedUrl(props.src);
});

// Current PDF URL - returns accessible PDF URL if enabled, otherwise original
const currentPdfUrl = computed(() => withPdfViewerParams(rawPdfUrl.value));

// Which viewer to render (Adobe PDF Embed or native browser iframe)
const { adobeEnabled } = usePdfViewer();

// Crash guard: when AdobePdfViewer emits 'failed' (SDK blocked, license check
// failed, CORS rejected, etc.) we flip this flag and the template falls
// through to the native iframe. Reset whenever the source URL or accessible
// preference changes so a new PDF gets a fresh attempt at Adobe.
const adobeFailed = ref(false);
watch(() => props.src, () => { adobeFailed.value = false; });
watch(accessibleEnabled, () => { adobeFailed.value = false; });

// Process PDF through accessibility pipeline (server-side) - uses signed URL approach
const processAccessiblePdf = async (pdfUrl) => {
    try {
        processingAccessible.value = true;

        const response = await $fetch('/public-api/pdf/accessible', {
            method: 'POST',
            body: {
                pdfUrl: pdfUrl,
                title: props.pdfTitle,
                lang: props.pdfLang,
                format: 'url',
                siteSlug: props.siteSlug,
                pageSlug: props.pageSlug,
                pageType: props.pageType
            }
        });

        currentAccessibleUrl.value = response.url;
        return currentAccessibleUrl.value;
    } catch (err) {
        console.error('Error getting accessible PDF URL:', err);
        throw err;
    } finally {
        processingAccessible.value = false;
    }
};

// Load PDF - fetch accessible URL if needed
const loadPdf = async () => {
    loading.value = true;
    error.value = '';

    try {
        const proxiedUrl = getProxiedUrl(props.src);

        if (accessibleEnabled.value) {
            try {
                await processAccessiblePdf(proxiedUrl);
            } catch (err) {
                console.error('Accessible PDF processing failed, loading original:', err);
                currentAccessibleUrl.value = null;
            }
        }

        loading.value = false;
    } catch (err) {
        console.error('Error loading PDF:', err);
        error.value = 'Failed to load PDF';
        loading.value = false;
    }
};

// Handle iframe load event
const handleIframeLoad = () => {
    loading.value = false;
};

// Handle the user's download choice from the modal
const handleDownloadChoice = async (type, closeModal) => {
    if (type === 'accessible') {
        try {
            // Ensure accessible URL is available
            if (!currentAccessibleUrl.value) {
                const proxiedUrl = getProxiedUrl(props.src);
                await processAccessiblePdf(proxiedUrl);
            }
            if (currentAccessibleUrl.value) {
                const link = document.createElement('a');
                link.href = currentAccessibleUrl.value + '?download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            console.error('Accessible PDF download failed, falling back to original:', err);
            window.open(getProxiedUrl(props.src), '_blank');
        }
    } else {
        // Original PDF - fetch and download since endpoint returns inline disposition
        try {
            const response = await fetch(getProxiedUrl(props.src));
            if (!response.ok) {
                throw new Error(`Download failed with status ${response.status}`);
            }
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = props.pdfTitle || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Download failed:', err);
            window.open(getProxiedUrl(props.src), '_blank');
        }
    }
    closeModal();
};

// Open download modal
const openDownloadModal = () => {
    const { open, close } = useModal({
        component: DownloadPdfModal,
        attrs: {
            showAccessibleOption: accessibleFeatureEnabled.value,
            onClose() {
                close();
            },
            async onDownload(type) {
                await handleDownloadChoice(type, close);
            }
        }
    });
    open();
};

// Watch for toggle changes - reload PDF when accessibility preference changes
watch(accessibleEnabled, () => {
    if (props.src) {
        currentAccessibleUrl.value = null;
        loadPdf();
    }
});

// Load PDF when component mounts or src changes
onMounted(() => {
    if (props.src) {
        loadPdf();
    }
});

watch(() => props.src, (newSrc) => {
    if (newSrc) {
        currentAccessibleUrl.value = null;
        loadPdf();
    }
});
</script>
